import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { hrLeadershipTMAPI } from '../utils/api';
import { isAdmin } from '../utils/auth';
import HRAttachmentField from '../components/HRAttachmentField';
import './DashboardPage.css';

const notifyHrDataChanged = () => globalThis.dispatchEvent(new Event('hrDataChanged'));

const getFiscalYearStartYear = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const month = d.getMonth();
  const year = d.getFullYear();
  return month >= 9 ? year : year - 1;
};

const formatYearLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`;

const getStoredHrYear = () => {
  const current = getFiscalYearStartYear(new Date());
  try {
    const raw = localStorage.getItem('hrSelectedYear');
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : current;
  } catch {
    return current;
  }
};

const formatCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return '0%';
  return `${value}%`;
};

const normalizeCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const getCompletionColor = (pct) => {
  if (pct === null || pct === undefined || Number.isNaN(Number(pct))) {
    return 'danger';
  }
  const value = Math.max(0, Math.min(100, Number(pct)));
  if (value >= 75) return 'success';
  if (value >= 40) return 'warning';
  return 'danger';
};

const MONTH_COLS = [
  { key: 'nov_25', label: 'Nov-25' },
  { key: 'dec_25', label: 'Dec-25' },
  { key: 'jan_26', label: 'Jan-26' },
  { key: 'feb_26', label: 'Feb-26' },
  { key: 'mar_26', label: 'Mar-26' },
  { key: 'apr_26', label: 'Apr-26' },
  { key: 'may_26', label: 'May-26' },
  { key: 'jun_26', label: 'Jun-26' },
  { key: 'jul_26', label: 'Jul-26' },
  { key: 'aug_26', label: 'Aug-26' },
  { key: 'sep_26', label: 'Sep-26' },
  { key: 'oct_26', label: 'Oct-26' },
];

const EMPTY_FORM = {
  actual_start_date: '',
  actual_end_date: '',
  completion_pct: '',
  responsibility: '',
  nov_25: '', dec_25: '', jan_26: '', feb_26: '', mar_26: '', apr_26: '',
  may_26: '', jun_26: '', jul_26: '', aug_26: '', sep_26: '', oct_26: '',
};

const AlertPropType = PropTypes.shape({
  type: PropTypes.string,
  message: PropTypes.string
});

const AttachmentPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  path: PropTypes.string
});

const LeadershipFormPropType = PropTypes.shape({
  responsibility: PropTypes.string,
  actual_start_date: PropTypes.string,
  actual_end_date: PropTypes.string,
  completion_pct: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nov_25: PropTypes.string,
  dec_25: PropTypes.string,
  jan_26: PropTypes.string,
  feb_26: PropTypes.string,
  mar_26: PropTypes.string,
  apr_26: PropTypes.string,
  may_26: PropTypes.string,
  jun_26: PropTypes.string,
  jul_26: PropTypes.string,
  aug_26: PropTypes.string,
  sep_26: PropTypes.string,
  oct_26: PropTypes.string
});

const SubSubParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  marks: PropTypes.number,
  description: PropTypes.string,
  guidelines: PropTypes.string,
  auditorDetails: PropTypes.string
});

const SubParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  marks: PropTypes.number,
  description: PropTypes.string,
  guidelines: PropTypes.string,
  auditorDetails: PropTypes.string,
  subSubParameters: PropTypes.arrayOf(SubSubParameterPropType)
});

const ParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  subParameters: PropTypes.arrayOf(SubParameterPropType).isRequired
});

const buildLeadershipMonthPayload = (formLike) => MONTH_COLS.reduce((acc, { key }) => {
  acc[key] = formLike?.[key] || null;
  return acc;
}, {});

const buildLeadershipUpsertPayload = ({
  departmentId,
  fyStartYear,
  parameterId,
  subParameterId,
  formLike,
  responsibilityOverride
}) => {
  const completionValue = formLike?.completion_pct === '' ? null : (formLike?.completion_pct ?? null);
  const responsibilityValue = responsibilityOverride === undefined
    ? (formLike?.responsibility || null)
    : (responsibilityOverride || null);

  return {
    department_id: departmentId,
    year_start: fyStartYear,
    parameter_id: parameterId,
    sub_parameter_id: subParameterId,
    actual_start_date: formLike?.actual_start_date || null,
    actual_end_date: formLike?.actual_end_date || null,
    completion_pct: completionValue,
    responsibility: responsibilityValue,
    ...buildLeadershipMonthPayload(formLike)
  };
};



export const INIT_PARAMETERS = [
  {
    id: 'talent-review-council',
    name: 'Talent Review Council',
    subParameters: [
      {
        id: 'trc-1',
        description: "Each business to institutionalise 'Talent Review Council' comprising of Business EXCO and formally meets on quarterly basis to review talent agenda for progressive action.",
        guidelines: "Evidence to show:\n- List of EXCO members in the Council\n- Quarterly meeting agenda and review / action points documented and shared within respective business EXCO meeting.",
        auditorDetails: "i) Talent review council and details of members. Share the presentation, MoM and review of action points of Dec'25, Mar'26 and Jun'26, Sept'26\nii) Please share evidence that the same take place on a quarterly basis.",
      },
    ],
  },
  {
    id: 'act-up-star-identification',
    name: 'ACT-UP in Place / Equivalent Star Identification Program',
    subParameters: [
      {
        id: 'act-up-a', label: 'A', marks: 30,
        description: 'Have in place an Institutionalised process of Star identification:\n(i) Non-Technical (Gen Mgt)\n(ii) Technical ACT-UP',
        guidelines: 'Scoring Matrix:\na) To run the ACT-UP process as per policy.\n●  Non-Technical : 15 Marks\n●  Technical ACT UP : 15 Marks',
        auditorDetails: 'Technical Act up:\n● Date of technical act up:\n● Date of star identification:\n● Date of communication of result along with anchor details:\n● Evidence to be provided that all stars and leaders identified have taken up their new roles within 3 months of announcement. The same should be signed off by HR heads\n● Evidence of enhanced role (including the date of such role enhancement)\n● Similar way share for Non-technical Act up',
      },
      {
        id: 'act-up-b', label: 'B', marks: 20,
        description: 'New Deliverables are assigned for all identified stars (as per standard deliverables template) and new deliverables to be uploaded in PMS portal post ACT-UP.',
        guidelines: 'b) All new deliverables are uploaded - Signed-Off document from BU HR Head (20 Marks)',
        auditorDetails: 'Technical Act up:\n● Date of technical act up:\n● Date of star identification:\n● Date of communication of result along with anchor details:\n● Evidence to be provided that all stars and leaders identified have taken up their new roles within 3 months of announcement. The same should be signed off by HR heads\n● Evidence of enhanced role (including the date of such role enhancement)\n● Similar way share for Non-technical Act up',
      },
      {
        id: 'act-up-c', label: 'C', marks: 10,
        description: 'Communication & Announcement of Results in the form of OA:',
        guidelines: 'c) Communication & Announcement of Result within two months of starting the program including anchors detail (30 Marks)',
        auditorDetails: 'Technical Act up:\n● Date of technical act up:\n● Date of star identification:\n● Date of communication of result along with anchor details:\n● Evidence to be provided that all stars and leaders identified have taken up their new roles within 3 months of announcement. The same should be signed off by HR heads\n● Evidence of enhanced role (including the date of such role enhancement)\n● Similar way share for Non-technical Act up',
      },
      {
        id: 'act-up-d', label: 'D', marks: 20,
        description: 'Anchors are mapped for the identified leaders and announced along with OA:',
        guidelines: 'c) Communication & Announcement of Result within two months of starting the program including anchors detail (30 Marks)',
        auditorDetails: 'Technical Act up:\n● Date of technical act up:\n● Date of star identification:\n● Date of communication of result along with anchor details:\n● Evidence to be provided that all stars and leaders identified have taken up their new roles within 3 months of announcement. The same should be signed off by HR heads\n● Evidence of enhanced role (including the date of such role enhancement)\n● Similar way share for Non-technical Act up',
      },
      {
        id: 'act-up-e', label: 'E', marks: 10,
        description: 'Elevated/Enhanced role within 3 months of result announcement',
        guidelines: 'd) Sign off document on all leaders taking elevated roles within 3 months of result announcement. (10 Marks)',
        auditorDetails: 'Technical Act up:\n● Date of technical act up:\n● Date of star identification:\n● Date of communication of result along with anchor details:\n● Evidence to be provided that all stars and leaders identified have taken up their new roles within 3 months of announcement. The same should be signed off by HR heads\n● Evidence of enhanced role (including the date of such role enhancement)\n● Similar way share for Non-technical Act up',
      },
    ],
  },
  { id: 'attrition', name: 'Attrition', subParameters: [
    {
      id: 'attrition-1',
      label: '1',
      marks: 50,
      description: "Attrition among Hi-POs:\nAttrition of 'Hi-POs' / 'leaders' identified (like via leadersconnect, chairman workshop, etc.) should not be more than 5%",
      guidelines: "Scoring Matrix for 1: To be computed for last 12 months.\n< 5% — 50 points\n> 5% to 8% — 30 points\n> 8% to 10% — 20 points\n> 10% — Nil points\n\n(Hi-POs to include Technical, Non-Technical Stars, Chairman & VC Growth Workshop leaders, initiatives driven from Group HR such as Management ACT UP, Emerging leaders, Enabling ACT UP, etc.)",
      auditorDetails: 'Refer Annexure 1.',
      subSubParameters: [],
    },
    {
      id: 'attrition-2',
      label: '2',
      marks: 50,
      description: 'Attrition in Key cohorts:',
      guidelines: "Scoring Matrix for 2 (For A, B & C): This should be computed for last 12 months.\nLess than 5%      : 10 Marks\n> 5% up to 8%     : 7.5 Marks\n> 8% up to 10%    : 5 Marks\n> 10%             : NIL\n\nFor D: To be checked for last 12 months. M9 and above to be considered.\n● If equal or less than male attrition — 100% marks\n● At 110% of male attrition — 80% marks\n● At 120% of male attrition — 60% marks\n● At 140% of male attrition — 40%\n● At 150% of male attrition — Nil",
      auditorDetails: 'Refer Annexure 1.',
      subSubParameters: [
        { id: 'attrition-2-a', label: 'A', marks: 10, description: 'Attrition among key positions (M4 & Above)' },
        { id: 'attrition-2-b', label: 'B', marks: 10, description: 'Attrition amongst laterals is less than 5%' },
        { id: 'attrition-2-c', label: 'C', marks: 10, description: 'Women workforce attrition is less than 5%' },
        { id: 'attrition-2-d', label: 'D', marks: 20, description: 'Female attrition should not be more than male attrition' },
      ],
    },
    {
      id: 'attrition-3',
      label: '3',
      marks: 30,
      description: 'Overall Attrition:',
      guidelines: "Scoring Matrix for 3:\nA) This should be computed for last 12 months.\n● Less than 10% — 20 Marks\n● >10% up to 12% — 15 Marks\n● >12% up to 14% — 10 Marks\n● >14% — Nil points\n\nB) Freshers\n● 0–1 year: 15%\n● 1–2 year: 10%\n● 2–3 year: 5%\n● More than 3 year: 2%\n(If one assertion is not fulfilled — 50% points (5 Marks) and if two or more not fulfilled — 0% points)",
      auditorDetails: 'Refer Annexure 1.',
      subSubParameters: [
        { id: 'attrition-3-a', label: 'A', marks: 20, description: 'Overall Attrition in the respective Business is below 10% — 20 points' },
        { id: 'attrition-3-b', label: 'B', marks: 10, description: 'Attrition amongst freshers (from Campus) — 10 points' },
      ],
    },
  ] },
  { id: 'hi-pos-stars-leaders', name: 'Hi-POs / Stars / Leaders (Chairman Workshop etc.) Holding Key Role / Positioning', subParameters: [
    {
      id: 'hi-pos-1',
      description: '● All Key Positions to be occupied by Hi-POs\n● All Hi-POs in M4 & above grade to be provided with challenging role\nIn case of any \'Gap/s\', action plan within 3 months is required.',
      guidelines: '● List of Key Positions along with mapping of all Stars in M4 & above grade to be documented. (In case of any \'Gap/s\', documented action within 3 months is required)\n● Evidence of challenging role to be provided.',
      auditorDetails: 'List of key positions along with names of employee occupying the same — Map whether those employees are Hi-POs or not.',
      subSubParameters: [],
    },
  ] },
  { id: 'stars-movements', name: 'Stars Movements', subParameters: [
    {
      id: 'stars-movements-1',
      description: "Structured system of 10% 'Hi-POs' Migration to other group companies / across business. (Focus on M5 & Above)",
      guidelines: "10% Hi-POs who have completed more than 4 years to be moved across businesses on annual basis:\n● 100% mark, if: Structured process is in place & 100% target achieved.\n● 75% marks, if: Structured process exists & 50% target achieved.\n● 25% marks, if process exists, but target not achieved / or / target achieved without structured process in place.",
      auditorDetails: "Evidence of criteria for identification of 10% Stars in place.\n\nNeed evidence as to 10% movement of stars has happened in the last one year to other group companies/across business i.e. from 1st Nov 2024 till 31st Oct 2025. (Please map list of stars provided with their last movement date to other businesses/group companies)",
      subSubParameters: [],
    },
  ] },
  { id: 'job-rotation', name: 'Job Rotation', subParameters: [
    {
      id: 'job-rotation-1',
      description: "Job rotation especially in Commercial, Finance (external facing), Marketing to be implemented (3 years for enabling function and 4-5 years for technical function). All job rotation/movements to take place as per Group guidelines and structured process to be in place.\n\nAny single deviation will result in zero scoring for entire section of Job Rotation and additional penalty of negative scoring.\n\nReduction of points if less than 50% movements are achieved for those in sensitive functions or in case of any deviation.",
      guidelines: "(A) While considering job rotation, job enlargement (handling same profile with higher responsibilities) / promotion would not be considered as job rotation.\n● 100% mark: If Structured process is in place & 100% target achieved.\n● 75% marks: If Structured process exists & 50% target achieved.\n● Reduction of 20 marks: If process exists, but target achieved is less than 50% / or target not achieved / target achieved without structured process in place.\n\n(B) Similar to above matrix applicable. Adequate details of employees with current and earlier profile is provided by entity. This would be applicable for sensitive positions.",
      auditorDetails: "Please share working to substantiate job rotation exists in every 3 years for enabling function, and 4-5 years for technical function. Please map target achieved as well.\n\nAlso, Refer Annexure 8.",
      subSubParameters: [],
    },
  ] },
  { id: 'succession-planning', name: 'Succession Planning', subParameters: [
    {
      id: 'succession-a', label: 'A', marks: 10,
      description: 'Succession planning guidelines & key critical role list are in place and approved by CEO.',
      guidelines: 'Succession planning guidelines and key critical role list to be documented and approved by CEO.',
      auditorDetails: 'Succession Planning guidelines in place to be shared along with succession planning details of last 2 years — which should be signed off by CEO.',
      subSubParameters: [],
    },
    {
      id: 'succession-b', label: 'B', marks: 50,
      description: '3 Level Successors of SBU/Business for all above mentioned key critical roles along with signed IDPs',
      guidelines: 'Scoring Guidelines for B:\ni) 100% Marks: If 3 levels of successors are identified for all key critical roles\nii) 70% Marks: If 2 levels of successors are identified for all key critical roles\niii) 50% Marks: If 1 level of successors are identified for all key roles\niv) Nil Marks: In any other case\n\nData of last 2 years to be provided as evidence. E.g. Whether created vacancies were filled-up by identified successor/s.',
      auditorDetails: 'Please share evidence of IDPs for identified successors for key positions in last 2 years.',
      subSubParameters: [],
    },
    {
      id: 'succession-c', label: 'C', marks: 20,
      description: 'Report of readiness of successors. Entire Succession Planning Exercise to be tracked digitally (readiness).',
      guidelines: 'Digital tracking of succession planning readiness to be in place and demonstrated.',
      auditorDetails: 'Digital report to be furnished for tracking and enabling succession planning online.',
      subSubParameters: [],
    },
    {
      id: 'succession-d', label: 'D', marks: 20,
      description: 'Vacancies were filled-up by identified successor/s. Businesses to furnish proof of successful succession process.',
      guidelines: 'Evidence of vacancies filled by identified successors to be provided for last 2 years.',
      auditorDetails: 'Succession Planning guidelines in place to be shared along with succession planning details of last 2 years — which should be signed off by CEO.',
      subSubParameters: [],
    },
  ] },
];

// Format slash-separated names for display: "A / B / C"
const formatResponsibility = (val) =>
  val ? val.split('/').map((s) => s.trim()).filter(Boolean).join(' / ') : '';

// ── Inline responsibility editor ───────────────────────────────────────────
// onSave: async function that persists the new value to DB immediately
const ResponsibilityField = ({ value, onSave, readOnly }) => {
  const admin = isAdmin();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      const message =
        typeof e === 'string'
          ? e
          : e?.message || e?.data?.message || 'Failed to save responsibility.';
      setError(message);
      setEditing(true);
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => { setDraft(value || ''); setEditing(false); setError(''); };

  const display = formatResponsibility(value);

  if (!admin || readOnly) {
    return (
      <div className="mb-3">
        <strong className="hr-responsibility">Responsibility: </strong>
        <span className="hr-responsibility">{display || <em className="text-muted">Not set</em>}</span>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <strong className="hr-responsibility">Responsibility: </strong>
      {editing ? (
        <span className="d-inline-flex align-items-center gap-1 ms-1 flex-wrap">
          <Form.Control
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Name1 / Name2"
            className="hr-inline-input"
            autoFocus
          />
          <Button variant="success" size="sm" className="py-0 px-1" onClick={confirm} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <FiCheck size={13} />}
          </Button>
          <Button variant="outline-secondary" size="sm" className="py-0 px-1" onClick={cancel} disabled={saving}>
            <FiX size={13} />
          </Button>
          {error && <small className="text-danger ms-2">{error}</small>}
        </span>
      ) : (
        <span className="hr-responsibility">
          {display || <em className="text-muted">Not set</em>}
          <Button variant="link" size="sm" className="p-0 ms-2 d-inline-flex align-items-center gap-1" onClick={() => setEditing(true)} title="Edit responsibility">
            <FiEdit2 size={13} />
            <span>Edit</span>
          </Button>
        </span>
      )}
    </div>
  );
};

ResponsibilityField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

// ── Sub-sub-parameter form (nested inside a sub-parameter) ────────────────
const SubSubParameterForm = ({ ssp, parameterId, subParamId, departmentId, fyStartYear, readOnly }) => {
  const compositeId = `${subParamId}:${ssp.id}`;
  const [form, setForm]           = useState(EMPTY_FORM);
  const [loading, setLoading]     = useState(true);
  const [hasRecord, setHasRecord] = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [alert, setAlert]         = useState(null);
  const [attachments, setAttachments] = useState([]);

  const fetchRecord = useCallback(async () => {
    if (!departmentId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await hrLeadershipTMAPI.getRecord(departmentId, parameterId, compositeId, fyStartYear);
      if (res?.data) {
        const d = res.data;
        setForm({
          actual_start_date: d.actual_start_date || '',
          actual_end_date:   d.actual_end_date   || '',
          completion_pct: d.completion_pct !== null && d.completion_pct !== undefined ? String(d.completion_pct) : '',
          responsibility: d.responsibility || '',
          nov_25: d.nov_25 || '', dec_25: d.dec_25 || '', jan_26: d.jan_26 || '',
          feb_26: d.feb_26 || '', mar_26: d.mar_26 || '', apr_26: d.apr_26 || '',
          may_26: d.may_26 || '', jun_26: d.jun_26 || '', jul_26: d.jul_26 || '',
          aug_26: d.aug_26 || '', sep_26: d.sep_26 || '', oct_26: d.oct_26 || '',
        });
        setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
        setHasRecord(true);
      } else {
        setAttachments([]);
        setHasRecord(false);
      }
    } catch { setAttachments([]); setHasRecord(false); } finally { setLoading(false); }
  }, [departmentId, parameterId, compositeId, fyStartYear]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const formRef = React.useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  const saveResponsibility = async (val) => {
    const f = formRef.current;
    try {
      await hrLeadershipTMAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: compositeId,
        actual_start_date: f.actual_start_date || null, actual_end_date: f.actual_end_date || null,
        completion_pct: f.completion_pct !== '' ? f.completion_pct : null,
        responsibility: val || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: f[key] || null }), {}),
      });
      setForm((prev) => ({ ...prev, responsibility: val }));
      setHasRecord(true);
      notifyHrDataChanged();
    } catch (e) { console.error('ssp saveResponsibility failed:', e); }
  };

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setAlert(null);
    try {
      await hrLeadershipTMAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: compositeId,
        actual_start_date: form.actual_start_date || null, actual_end_date: form.actual_end_date || null,
        completion_pct: form.completion_pct !== '' ? form.completion_pct : null,
        responsibility: form.responsibility || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: form[key] || null }), {}),
      });
      setAlert({ type: 'success', message: 'Saved.' });
      setHasRecord(true); setEditMode(false);
      notifyHrDataChanged();
    } catch { setAlert({ type: 'danger', message: 'Failed to save.' }); }
    finally { setSaving(false); }
  };

  const handleCancel = () => { fetchRecord(); setEditMode(false); setAlert(null); };
  const displayVal = (val) => val || <span className="text-muted">—</span>;

  const handleUpload = async (files) => {
    if (readOnly) return;
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    fd.append('department_id', departmentId);
    fd.append('year_start', String(fyStartYear));
    fd.append('parameter_id', parameterId);
    fd.append('sub_parameter_id', compositeId);
    const res = await hrLeadershipTMAPI.uploadAttachment(fd);
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    setHasRecord(true);
    notifyHrDataChanged();
  };

  const handleDeleteAttachment = async (attachmentIndex) => {
    if (readOnly) return;
    const res = await hrLeadershipTMAPI.deleteAttachment({ department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: compositeId, attachment_index: attachmentIndex });
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    notifyHrDataChanged();
  };

  const handleDownload = (attachmentIndex) => {
    hrLeadershipTMAPI.downloadAttachment(departmentId, parameterId, compositeId, attachmentIndex, fyStartYear).then((blob) => {
      const url = globalThis.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a'); a.href = url; a.download = attachments[attachmentIndex]?.name || 'attachment';
      document.body.appendChild(a); a.click(); a.remove(); globalThis.URL.revokeObjectURL(url);
    });
  };

  const formFields = (
    <>
      <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
      <Row className="g-3 mb-3">
        <Col md={4}><Form.Group><Form.Label className="hr-form-label-sm">Actual Start Date</Form.Label><Form.Control type="date" name="actual_start_date" value={form.actual_start_date} onChange={handleChange} size="sm" disabled={readOnly} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label className="hr-form-label-sm">Actual End Date</Form.Label><Form.Control type="date" name="actual_end_date" value={form.actual_end_date} onChange={handleChange} size="sm" disabled={readOnly} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label className="hr-form-label-sm">% Completion</Form.Label><Form.Control type="number" name="completion_pct" value={form.completion_pct} onChange={handleChange} min={0} max={100} step={0.01} placeholder="0-100" size="sm" disabled={readOnly} /></Form.Group></Col>
      </Row>
      <Row className="g-2 mb-3">
        {MONTH_COLS.map(({ key, label }) => (
          <Col key={key} xs={6} sm={4} md={3} lg={2}>
            <Form.Group><Form.Label className="hr-form-label-xs">{label}</Form.Label><Form.Control type="text" name={key} value={form[key]} onChange={handleChange} size="sm" placeholder={label} disabled={readOnly} /></Form.Group>
          </Col>
        ))}
      </Row>
      <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
    </>
  );

  return (
    <Card className="mb-2 border hr-subsub-card">
      <Card.Body className="py-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="badge bg-info text-dark hr-label-badge-sm">{ssp.label}</span>
          {ssp.marks && <span className="badge hr-marks-badge-sm">{ssp.marks} Marks</span>}
        </div>
        <div className="mb-2">
          <strong className="hr-section-heading-sm">Parameter</strong>
          <div className="hr-section-text-sm">{ssp.description}</div>
        </div>
        {loading ? (
          <div className="py-2"><Spinner animation="border" size="sm" /></div>
        ) : !hasRecord ? (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-1 mb-2 hr-alert-sm">{alert.message}</Alert>}
            <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
            </Button>
          </Form>
        ) : !editMode ? (
          <>
            <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
            <Row className="g-2 mb-2">
              <Col md={4}><div className="hr-detail-row-sm"><strong>Start:</strong> {displayVal(form.actual_start_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row-sm"><strong>End:</strong> {displayVal(form.actual_end_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row-sm"><strong>% Completion:</strong> <span className={`text-${getCompletionColor(form.completion_pct || 0)}`}>{formatCompletionValue(form.completion_pct)}</span></div></Col>
            </Row>
            <Row className="g-1 mb-2">
              {MONTH_COLS.map(({ key, label }) => (
                <Col key={key} xs={6} sm={4} md={3} lg={2}>
                  <div className="hr-month-detail-sm"><span className="text-muted">{label}: </span>{displayVal(form[key])}</div>
                </Col>
              ))}
            </Row>
            <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
            {alert && <Alert variant={alert.type} className="py-1 mb-2 hr-alert-sm">{alert.message}</Alert>}
            {!readOnly && (
              <Button variant="outline-primary" size="sm" onClick={() => { setAlert(null); setEditMode(true); }}>
                <FiEdit2 size={12} className="me-1" />Edit
              </Button>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-1 mb-2 hr-alert-sm">{alert.message}</Alert>}
            <div className="d-flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>
                {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
              </Button>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleCancel} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

SubSubParameterForm.propTypes = {
  ssp: SubSubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  subParamId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};
	
// ── Sub-parameter form ─────────────────────────────────────────────────────
const toLeadershipErrorMessage = (e, fallback = 'Failed to save. Please try again.') => (
  typeof e === 'string' ? e : e?.message || e?.data?.message || fallback
);

const mapLeadershipRecordToForm = (data) => ({
  actual_start_date: data?.actual_start_date || '',
  actual_end_date: data?.actual_end_date || '',
  completion_pct: data?.completion_pct !== null && data?.completion_pct !== undefined ? String(data.completion_pct) : '',
  responsibility: data?.responsibility || '',
  nov_25: data?.nov_25 || '', dec_25: data?.dec_25 || '', jan_26: data?.jan_26 || '',
  feb_26: data?.feb_26 || '', mar_26: data?.mar_26 || '', apr_26: data?.apr_26 || '',
  may_26: data?.may_26 || '', jun_26: data?.jun_26 || '', jul_26: data?.jul_26 || '',
  aug_26: data?.aug_26 || '', sep_26: data?.sep_26 || '', oct_26: data?.oct_26 || ''
});

const loadLeadershipSubParamRecord = async ({ departmentId, parameterId, subParameterId, fyStartYear }) => {
  if (!departmentId) return { form: EMPTY_FORM, attachments: [], hasRecord: false };
  try {
    const res = await hrLeadershipTMAPI.getRecord(departmentId, parameterId, subParameterId, fyStartYear);
    if (res?.data) {
      return {
        form: mapLeadershipRecordToForm(res.data),
        attachments: Array.isArray(res.data.attachments) ? res.data.attachments : [],
        hasRecord: true
      };
    }
  } catch {
    // ignore
  }
  return { form: { ...EMPTY_FORM, responsibility: '' }, attachments: [], hasRecord: false };
};

const upsertLeadershipSubParamRecord = async ({
  departmentId,
  fyStartYear,
  parameterId,
  subParameterId,
  formLike,
  responsibilityOverride
}) => (
  hrLeadershipTMAPI.upsertRecord(buildLeadershipUpsertPayload({
    departmentId,
    fyStartYear,
    parameterId,
    subParameterId,
    formLike,
    responsibilityOverride
  }))
);

const downloadBlobAsFile = (blob, filename) => {
  const url = globalThis.URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  globalThis.URL.revokeObjectURL(url);
};

const computeAutoCompletionPct = async ({ hasSubSub, departmentId, parameterId, subParamId, subSubParameters, fyStartYear }) => {
  if (!hasSubSub || !departmentId) return null;
  const results = await Promise.all(
    (subSubParameters || []).map((ssp) =>
      hrLeadershipTMAPI.getRecord(departmentId, parameterId, `${subParamId}:${ssp.id}`, fyStartYear).catch(() => null)
    )
  );
  const pcts = results
    .map((r) => r?.data)
    .filter((d) => d && d.completion_pct !== null && d.completion_pct !== undefined)
    .map((d) => parseFloat(d.completion_pct));
  if (!pcts.length) return 0;
  const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
  return Math.round(avg * 10) / 10;
};

const useHrDataChangeCounter = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleHrDataChanged = () => setRefreshKey((value) => value + 1);
    globalThis.addEventListener('hrDataChanged', handleHrDataChanged);
    return () => globalThis.removeEventListener('hrDataChanged', handleHrDataChanged);
  }, []);

  return refreshKey;
};

const useAutoComputedCompletionPct = ({ hasSubSub, departmentId, parameterId, subParam, fyStartYear, refreshKey }) => {
  const [autoComputedPct, setAutoComputedPct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    computeAutoCompletionPct({
      hasSubSub,
      departmentId,
      parameterId,
      subParamId: subParam.id,
      subSubParameters: subParam.subSubParameters,
      fyStartYear
    }).then((pct) => {
      if (!cancelled && pct !== null) setAutoComputedPct(pct);
    });
    return () => { cancelled = true; };
  }, [hasSubSub, departmentId, parameterId, subParam.id, subParam.subSubParameters, fyStartYear, refreshKey]);

  return autoComputedPct;
};

const SubParameterHeader = ({ subParam, hasSubSub, autoComputedPct }) => (
  <>
    {'label' in subParam && (
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="badge bg-primary hr-label-badge">{subParam.label}</span>
        {subParam.marks && <span className="badge hr-marks-badge">{subParam.marks} Marks</span>}
      </div>
    )}

    <div className="mb-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
        <strong className="hr-section-heading">Parameter</strong>
        {hasSubSub && (
          <div className="d-flex flex-column align-items-end">
            <div className={`hr-completion-chip text-${getCompletionColor(normalizeCompletionValue(autoComputedPct))}`}>
              {formatCompletionValue(normalizeCompletionValue(autoComputedPct))}
            </div>
            <div className="hr-progress hr-inline-progress mt-1" style={{ '--hr-progress-width': `${normalizeCompletionValue(autoComputedPct)}%` }}>
              <div className={`hr-progress-fill hr-progress-${getCompletionColor(normalizeCompletionValue(autoComputedPct))}`} />
            </div>
          </div>
        )}
      </div>
      <p className="mb-0 hr-section-text">{subParam.description}</p>
    </div>

    <div className="mb-3">
      <strong className="hr-section-heading">Guidelines for Scoring Matrix</strong>
      <pre className="mt-1 mb-0 hr-section-pre">
        {subParam.guidelines}
      </pre>
    </div>

    <div className="mb-3">
      <strong className="hr-section-heading">Details Required by Auditors</strong>
      <pre className="mt-1 mb-0 hr-section-pre">
        {subParam.auditorDetails}
      </pre>
    </div>
  </>
);

SubParameterHeader.propTypes = {
  subParam: SubParameterPropType.isRequired,
  hasSubSub: PropTypes.bool.isRequired,
  autoComputedPct: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const SubParameterHasSubSubCard = ({ subParam, parameterId, departmentId, fyStartYear, readOnly }) => {
  const hasSubSub = true;
  const refreshKey = useHrDataChangeCounter();
  const autoComputedPct = useAutoComputedCompletionPct({ hasSubSub, departmentId, parameterId, subParam, fyStartYear, refreshKey });

  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        <SubParameterHeader subParam={subParam} hasSubSub={hasSubSub} autoComputedPct={autoComputedPct} />

        {subParam.subSubParameters && subParam.subSubParameters.length > 0 && (
          <div className="mb-3">
            <strong className="hr-form-label">Sub-Parameters</strong>
            <div className="mt-2">
              {subParam.subSubParameters.map((ssp) => (
                <SubSubParameterForm
                  key={ssp.id}
                  ssp={ssp}
                  parameterId={parameterId}
                  subParamId={subParam.id}
                  departmentId={departmentId}
                  fyStartYear={fyStartYear}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

SubParameterHasSubSubCard.propTypes = {
  subParam: SubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

const SubParameterSimpleCreateForm = ({ formFields, alert, saving, handleSubmit }) => (
  <Form onSubmit={handleSubmit}>
    {formFields}
    {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
    <Button type="submit" variant="primary" size="sm" disabled={saving}>
      {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
    </Button>
  </Form>
);

SubParameterSimpleCreateForm.propTypes = {
  formFields: PropTypes.node.isRequired,
  alert: AlertPropType,
  saving: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

const SubParameterSimpleReadOnlyView = ({
  form,
  displayVal,
  attachments,
  handleUpload,
  handleDeleteAttachment,
  handleDownload,
  alert,
  readOnly,
  onEdit,
  saveResponsibility
}) => (
  <>
    <ResponsibilityField
      value={form.responsibility}
      onSave={saveResponsibility}
      readOnly={readOnly}
    />
    <Row className="g-3 mb-3">
      <Col md={4}><div className="hr-detail-row"><strong>Actual Start Date:</strong> {displayVal(form.actual_start_date)}</div></Col>
      <Col md={4}><div className="hr-detail-row"><strong>Actual End Date:</strong> {displayVal(form.actual_end_date)}</div></Col>
      <Col md={4}><div className="hr-detail-row"><strong>% Completion:</strong> <span className={`text-${getCompletionColor(form.completion_pct || 0)}`}>{formatCompletionValue(form.completion_pct)}</span></div></Col>
    </Row>
    <Row className="g-2 mb-3">
      {MONTH_COLS.map(({ key, label }) => (
        <Col key={key} xs={6} sm={4} md={3} lg={2}>
          <div className="hr-month-detail">
            <div className="text-muted">{label}</div>
            <div>{displayVal(form[key])}</div>
          </div>
        </Col>
      ))}
    </Row>
    <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
    {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
    {!readOnly && (
      <Button variant="outline-primary" size="sm" onClick={onEdit}>
        <FiEdit2 size={13} className="me-1" />Edit
      </Button>
  )}
  </>
);

SubParameterSimpleReadOnlyView.propTypes = {
  form: LeadershipFormPropType.isRequired,
  displayVal: PropTypes.func.isRequired,
  attachments: PropTypes.arrayOf(AttachmentPropType).isRequired,
  handleUpload: PropTypes.func.isRequired,
  handleDeleteAttachment: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  alert: AlertPropType,
  readOnly: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  saveResponsibility: PropTypes.func.isRequired
};

const SubParameterSimpleEditForm = ({ formFields, alert, saving, readOnly, handleSubmit, handleCancel }) => (
  <Form onSubmit={handleSubmit}>
    {formFields}
    {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
    <div className="d-flex gap-2">
      <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>
        {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
      </Button>
      <Button type="button" variant="outline-secondary" size="sm" onClick={handleCancel} disabled={saving}>Cancel</Button>
    </div>
  </Form>
);

SubParameterSimpleEditForm.propTypes = {
  formFields: PropTypes.node.isRequired,
  alert: AlertPropType,
  saving: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired
};

const SubParameterSimpleBody = ({
  loading,
  hasRecord,
  editMode,
  formFields,
  alert,
  saving,
  readOnly,
  handleSubmit,
  handleCancel,
  form,
  displayVal,
  attachments,
  handleUpload,
  handleDeleteAttachment,
  handleDownload,
  onEdit,
  saveResponsibility
}) => {
  if (loading) return <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>;
  if (!hasRecord) return <SubParameterSimpleCreateForm formFields={formFields} alert={alert} saving={saving} handleSubmit={handleSubmit} />;
  if (!editMode) {
    return (
      <SubParameterSimpleReadOnlyView
        form={form}
        displayVal={displayVal}
        attachments={attachments}
        handleUpload={handleUpload}
        handleDeleteAttachment={handleDeleteAttachment}
        handleDownload={handleDownload}
        alert={alert}
        readOnly={readOnly}
        onEdit={onEdit}
        saveResponsibility={saveResponsibility}
      />
    );
  }
  return <SubParameterSimpleEditForm formFields={formFields} alert={alert} saving={saving} readOnly={readOnly} handleSubmit={handleSubmit} handleCancel={handleCancel} />;
};

SubParameterSimpleBody.propTypes = {
  loading: PropTypes.bool.isRequired,
  hasRecord: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  formFields: PropTypes.node.isRequired,
  alert: AlertPropType,
  saving: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  form: LeadershipFormPropType.isRequired,
  displayVal: PropTypes.func.isRequired,
  attachments: PropTypes.arrayOf(AttachmentPropType).isRequired,
  handleUpload: PropTypes.func.isRequired,
  handleDeleteAttachment: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  saveResponsibility: PropTypes.func.isRequired
};

const SubParameterSimpleCard = ({ subParam, parameterId, departmentId, fyStartYear, readOnly }) => {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [loading, setLoading]     = useState(true);
  const [hasRecord, setHasRecord] = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [alert, setAlert]         = useState(null);
  const [attachments, setAttachments] = useState([]);

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    const loaded = await loadLeadershipSubParamRecord({
      departmentId,
      parameterId,
      subParameterId: subParam.id,
      fyStartYear
    });
    setForm(loaded.form);
    setAttachments(loaded.attachments);
    setHasRecord(loaded.hasRecord);
    setLoading(false);
  }, [departmentId, parameterId, subParam.id, fyStartYear]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

		  const handleSubmit = async (e) => {
		    e.preventDefault();
		    if (readOnly) return;
		    setSaving(true); setAlert(null);
		    try {
		      await upsertLeadershipSubParamRecord({
		        departmentId,
		        fyStartYear,
		        parameterId,
		        subParameterId: subParam.id,
		        formLike: form
		      });
		      setAlert({ type: 'success', message: 'Saved successfully.' });
		      setHasRecord(true);
		      setEditMode(false);
		      notifyHrDataChanged();
	    } catch (e) {
	      setAlert({ type: 'danger', message: toLeadershipErrorMessage(e) });
	    } finally { setSaving(false); }
	  };

  const handleCancel = () => { fetchRecord(); setEditMode(false); setAlert(null); };

  const displayVal = (val) => val || <span className="text-muted">—</span>;

  const handleUpload = async (files) => {
    if (readOnly) return;
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    fd.append('department_id', departmentId);
    fd.append('year_start', String(fyStartYear));
    fd.append('parameter_id', parameterId);
    fd.append('sub_parameter_id', subParam.id);
    const res = await hrLeadershipTMAPI.uploadAttachment(fd);
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    setHasRecord(true);
    notifyHrDataChanged();
  };

  const handleDeleteAttachment = async (attachmentIndex) => {
    if (readOnly) return;
    const res = await hrLeadershipTMAPI.deleteAttachment({ department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id, attachment_index: attachmentIndex });
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    notifyHrDataChanged();
  };

  const handleDownload = (attachmentIndex) => {
    hrLeadershipTMAPI
      .downloadAttachment(departmentId, parameterId, subParam.id, attachmentIndex, fyStartYear)
      .then((blob) => downloadBlobAsFile(blob, attachments[attachmentIndex]?.name || 'attachment'));
  };

  // Save responsibility immediately to DB — uses a ref to always have fresh form values
  const formRef = React.useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

		  const saveResponsibility = async (val) => {
		    const currentForm = formRef.current;
		    try {
		      await upsertLeadershipSubParamRecord({
		        departmentId,
		        fyStartYear,
		        parameterId,
		        subParameterId: subParam.id,
		        formLike: currentForm,
		        responsibilityOverride: val
		      });
		      setForm((prev) => ({ ...prev, responsibility: val }));
		      setHasRecord(true);
		    } catch (e) {
		      console.error('saveResponsibility failed:', e);
	      throw e;
    }
	  };
	
	  const hasSubSub = false;
	  const autoComputedPct = null;
	
	  const formFields = (
	    <>
	      {/* Responsibility — editable by admin, always saves to DB immediately */}
      <ResponsibilityField
        value={form.responsibility}
        onSave={saveResponsibility}
      />
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="hr-form-label">Actual Start Date</Form.Label>
            <Form.Control type="date" name="actual_start_date" value={form.actual_start_date} onChange={handleChange} size="sm" />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="hr-form-label">Actual End Date</Form.Label>
            <Form.Control type="date" name="actual_end_date" value={form.actual_end_date} onChange={handleChange} size="sm" />
          </Form.Group>
	        </Col>
	        {/* Completion % — editable if no sub-sub-params, read-only (auto-derived) if has sub-sub-params */}
	        <Col md={4}>
	          <Form.Group>
	            <Form.Label className="hr-form-label">% Completion</Form.Label>
	            <Form.Control type="number" name="completion_pct" value={form.completion_pct} onChange={handleChange} min={0} max={100} step={0.01} placeholder="0 – 100" size="sm" />
	          </Form.Group>
	        </Col>
	      </Row>
      <Row className="g-2 mb-3">
        {MONTH_COLS.map(({ key, label }) => (
          <Col key={key} xs={6} sm={4} md={3} lg={2}>
            <Form.Group>
              <Form.Label className="hr-form-label-sm">{label}</Form.Label>
              <Form.Control type="text" name={key} value={form[key]} onChange={handleChange} size="sm" placeholder={label} />
            </Form.Group>
          </Col>
        ))}
      </Row>
      <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
    </>
  );

	  return (
	    <Card className="shadow-sm mb-3">
	      <Card.Body>
	        <SubParameterHeader subParam={subParam} hasSubSub={hasSubSub} autoComputedPct={autoComputedPct} />
	
	        <hr />

	        <SubParameterSimpleBody
	          loading={loading}
	          hasRecord={hasRecord}
	          editMode={editMode}
	          formFields={formFields}
	          alert={alert}
	          saving={saving}
	          readOnly={readOnly}
	          handleSubmit={handleSubmit}
	          handleCancel={handleCancel}
	          form={form}
	          displayVal={displayVal}
	          attachments={attachments}
	          handleUpload={handleUpload}
	          handleDeleteAttachment={handleDeleteAttachment}
	          handleDownload={handleDownload}
	          onEdit={() => { setAlert(null); setEditMode(true); }}
	          saveResponsibility={saveResponsibility}
	        />
	      </Card.Body>
	    </Card>
  );
};

SubParameterSimpleCard.propTypes = {
  subParam: SubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

const SubParameterForm = ({ subParam, parameterId, departmentId, fyStartYear, readOnly }) => {
  const hasSubSub = Array.isArray(subParam?.subSubParameters) && subParam.subSubParameters.length > 0;
  if (hasSubSub) {
    return (
      <SubParameterHasSubSubCard
        subParam={subParam}
        parameterId={parameterId}
        departmentId={departmentId}
        fyStartYear={fyStartYear}
        readOnly={readOnly}
      />
    );
  }

  return (
    <SubParameterSimpleCard
      subParam={subParam}
      parameterId={parameterId}
      departmentId={departmentId}
      fyStartYear={fyStartYear}
      readOnly={readOnly}
    />
  );
};

SubParameterForm.propTypes = {
  subParam: SubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

/**
 * Compute effective completion % for a parameter given a map of DB records.
 *
 * Rules:
 *  - Sub-param with NO sub-sub-params → use its own completion_pct from DB.
 *  - Sub-param WITH sub-sub-params → auto-derive as average of its sub-sub-params'
 *    completion_pct values (composite key = `spId:sspId`). The parent row's own
 *    completion_pct is ignored (it is auto-calculated, not manually filled).
 *  - Parameter completion = average of all sub-params' effective completion %.
 *    Only sub-params that have at least one filled value contribute to the average.
 */
const computeParameterCompletion = (param, recordMap) => {
  const subPctValues = [];

  param.subParameters.forEach((sp) => {
    const hasSubSub = sp.subSubParameters && sp.subSubParameters.length > 0;

    if (!hasSubSub) {
      // Simple sub-param: use its own DB record's completion_pct
      const rec = recordMap[sp.id];
      if (rec && rec.completion_pct !== null && rec.completion_pct !== undefined) {
        subPctValues.push(parseFloat(rec.completion_pct));
      }
    } else {
      // Sub-param with sub-sub-params: average the sub-sub-params' completion_pct
      const sspPcts = sp.subSubParameters
        .map((ssp) => recordMap[`${sp.id}:${ssp.id}`])
        .filter((r) => r && r.completion_pct !== null && r.completion_pct !== undefined)
        .map((r) => parseFloat(r.completion_pct));

      if (sspPcts.length > 0) {
        const sspAvg = sspPcts.reduce((a, b) => a + b, 0) / sspPcts.length;
        subPctValues.push(sspAvg);
      }
    }
  });

  if (subPctValues.length === 0) return null;
  const avg = subPctValues.reduce((a, b) => a + b, 0) / subPctValues.length;
  return Math.round(avg * 10) / 10;
};

// ── Tile with DB-loaded responsibility & auto-computed completion % ────────
const ParameterTile = ({ param, departmentId, fyStartYear, onClick }) => {
  const [responsibility, setResponsibility] = useState('');
  const [completionPct, setCompletionPct] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleHrDataChanged = () => setRefreshKey((value) => value + 1);
    globalThis.addEventListener('hrDataChanged', handleHrDataChanged);
    return () => globalThis.removeEventListener('hrDataChanged', handleHrDataChanged);
  }, []);

  useEffect(() => {
    if (!departmentId || param.subParameters.length === 0) return;
    hrLeadershipTMAPI.getParameterRecords(departmentId, param.id, fyStartYear)
      .then((res) => {
        const records = res?.data || [];
        if (records.length === 0) {
          setResponsibility('');
          setCompletionPct(0);
          return;
        }

        // Collect unique responsibility names across all records
        const names = [...new Set(
          records.map((r) => r.responsibility).filter(Boolean)
        )].join(' / ');
        setResponsibility(names);

        // Build a map keyed by sub_parameter_id for O(1) lookup
        const recordMap = {};
        records.forEach((r) => { recordMap[r.sub_parameter_id] = r; });

        setCompletionPct(computeParameterCompletion(param, recordMap));
      })
      .catch(() => {
        setResponsibility('');
        setCompletionPct(0);
      });
  }, [departmentId, param, refreshKey, fyStartYear]);

  const normalizedCompletionPct = normalizeCompletionValue(completionPct);
  const pctColor = getCompletionColor(normalizedCompletionPct);

  return (
    <Card className="shadow-sm h-100 hr-tile-card" onClick={onClick}>
      {responsibility && (
        <div className="hr-tile-responsibility">
          {responsibility}
        </div>
      )}
      <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
        <h6 className="mb-0 text-center">{param.name}</h6>
        <div className="mt-3 w-100">
          <div className="d-flex justify-content-between mb-1 hr-tile-meta">
            <span className="text-muted">Completion</span>
            <span className={`hr-tile-percent text-${pctColor}`}>{formatCompletionValue(normalizedCompletionPct)}</span>
          </div>
          <div className="hr-progress" style={{ '--hr-progress-width': `${normalizedCompletionPct}%` }}>
            <div className={`hr-progress-fill hr-progress-${pctColor}`} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

ParameterTile.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};
const ParameterDetail = ({ param, departmentId, onBack, onBackToMain, fyStartYear, readOnly }) => (
  <Container fluid className="dashboard-page py-4">
    <div className="d-flex align-items-center gap-2 mb-4">
      <Button variant="outline-secondary" size="sm" onClick={onBack}>
        <FiArrowLeft size={14} className="me-1" />Back
      </Button>
      <Button variant="outline-primary" size="sm" onClick={onBackToMain}>
        <FiArrowLeft size={14} className="me-1" />HR Main Dashboard
      </Button>
      <h5 className="mb-0">{param.name}</h5>
    </div>

    {param.subParameters.length === 0 ? (
      <p className="text-muted">No sub-parameters defined yet.</p>
    ) : (
      param.subParameters.map((sp) => (
        <SubParameterForm
          key={sp.id}
          subParam={sp}
          parameterId={param.id}
          departmentId={departmentId}
          fyStartYear={fyStartYear}
          readOnly={readOnly}
        />
      ))
    )}
  </Container>
);

ParameterDetail.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func.isRequired,
  onBackToMain: PropTypes.func.isRequired,
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

// ── Main page ──────────────────────────────────────────────────────────────
const HRLeadershipTMDashboardPage = ({ departmentId = null }) => {
  const navigate = useNavigate();
  const [fyStartYear, setFyStartYear] = useState(getStoredHrYear());
  const [selectedParam, setSelectedParam] = useState(null);

  const currentFy = getFiscalYearStartYear(new Date());
  const readOnly = !isAdmin() && fyStartYear !== currentFy;

  useEffect(() => {
    try { localStorage.setItem('hrSelectedYear', String(fyStartYear)); } catch {}
  }, [fyStartYear]);

  const goToHrMainDashboard = () => {
    localStorage.setItem('selectedProject', JSON.stringify({
      id: 'hr-main-dashboard',
      name: 'HR Main Dashboard',
      departmentId,
    }));
    globalThis.dispatchEvent(new Event('projectChanged'));
    navigate('/dashboard', { replace: true });
  };

  if (selectedParam) {
    return (
      <ParameterDetail
        param={selectedParam}
        departmentId={departmentId}
        onBack={() => setSelectedParam(null)}
        onBackToMain={goToHrMainDashboard}
        fyStartYear={fyStartYear}
        readOnly={readOnly}
      />
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <Button variant="outline-primary" size="sm" onClick={goToHrMainDashboard}>
          <FiArrowLeft size={14} className="me-1" />HR Main Dashboard
        </Button>
        <h5 className="mb-0">Leadership &amp; TM</h5>
        <div className="ms-auto d-flex align-items-center gap-2">
          {readOnly && <span className="text-muted small">Read-only (previous year)</span>}
          <Form.Select size="sm" value={fyStartYear} onChange={(e) => setFyStartYear(Number(e.target.value))} style={{ width: 140 }}>
            {[currentFy - 1, currentFy, currentFy + 1].map((y) => (
              <option key={y} value={y}>{formatYearLabel(y)}</option>
            ))}
          </Form.Select>
        </div>
      </div>
      <Row className="g-3 row-cols-1 row-cols-md-2 row-cols-lg-3">
        {INIT_PARAMETERS.map((param) => (
          <Col key={param.id}>
            <ParameterTile
              param={param}
              departmentId={departmentId}
              fyStartYear={fyStartYear}
              onClick={() => setSelectedParam(param)}
            />
          </Col>
        ))}
      </Row>
    </Container>
  );
};

HRLeadershipTMDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HRLeadershipTMDashboardPage;
